import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CreditCard, CheckCircle2, MapPin, Calendar, Clock, Users, CalendarPlus } from "lucide-react";
import { googleCalendarUrl } from "@/lib/calendar";

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${id}`)
      .then(({ data }) => setBooking(data))
      .catch(() => toast.error(t("detail.notFound")))
      .finally(() => setLoading(false));
  }, [id, t]);

  const payDeposit = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 pt-40 text-center">
          <h1 className="font-serif text-3xl text-white">{t("detail.notFound")}</h1>
          <Button className="mt-6 rounded-full bg-white text-black" onClick={() => navigate("/book")}>{t("detail.newReservation")}</Button>
        </div>
      </div>
    );
  }

  const paid = booking.payment_status === "deposit_paid";
  const isQuote = booking.quote_required || booking.payment_status === "quote_requested";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-32 pb-24">
        <h1 className="font-serif font-light text-4xl sm:text-5xl text-white mb-2">
          {paid ? t("detail.confirmed") : isQuote ? t("detail.received") : t("detail.secure")}
        </h1>
        <p className="text-muted-foreground mb-10">{t("detail.reference")} #{booking.id}</p>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 rounded-lg border border-white/10 bg-card p-8 space-y-5">
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <h3 className="font-serif text-2xl text-white">{booking.service_label}</h3>
              <span className={`text-xs uppercase tracking-luxe px-3 py-1.5 rounded-full border ${
                booking.status === "confirmed" ? "text-white border-white/40 bg-white/10" : "text-muted-foreground border-white/15"}`}>
                {booking.status}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-5 text-sm">
              <div className="flex items-center gap-3 text-white/80"><Calendar className="h-4 w-4 text-muted-foreground" /> {booking.date}</div>
              <div className="flex items-center gap-3 text-white/80"><Clock className="h-4 w-4 text-muted-foreground" /> {booking.time}{booking.hours ? ` · ${booking.hours}h` : ""}</div>
              <div className="flex items-center gap-3 text-white/80"><MapPin className="h-4 w-4 text-muted-foreground" /> {booking.pickup_location}</div>
              {booking.dropoff_location ? (
                <div className="flex items-center gap-3 text-white/80"><MapPin className="h-4 w-4 text-muted-foreground" /> {booking.dropoff_location}</div>
              ) : booking.package_label ? (
                <div className="flex items-center gap-3 text-white/80"><MapPin className="h-4 w-4 text-muted-foreground" /> {booking.package_label}</div>
              ) : null}
              <div className="flex items-center gap-3 text-white/80"><Users className="h-4 w-4 text-muted-foreground" /> {booking.passengers} {t("detail.passengers")}</div>
            </div>
            {booking.special_requests && (
              <div className="pt-4 border-t border-white/10 text-sm">
                <p className="text-muted-foreground mb-1">{t("booking.requests")}</p>
                <p className="text-white/80">{booking.special_requests}</p>
              </div>
            )}
          </div>

          <div className="lg:col-span-1 rounded-lg border border-white/10 bg-card p-8">
            <p className="text-xs uppercase tracking-luxe text-muted-foreground mb-6">{t("detail.payment")}</p>
            {isQuote ? (
              <div className="rounded-lg border border-white/10 bg-secondary/50 p-4" data-testid="quote-requested-note">
                <p className="text-white text-sm font-medium mb-1">{t("detail.quoteRequested")}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t("detail.quoteNote", { vehicle: booking.vehicle })}
                </p>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">{t("detail.estimatedTotal")}</span><span className="text-white">${booking.estimated_price}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("detail.deposit")}</span><span className="text-white">${booking.deposit_amount}</span></div>
                <div className="h-px bg-white/10 my-3" />
                <div className="flex justify-between"><span className="text-white">{t("detail.dueToday")}</span><span className="text-white font-serif text-2xl">${booking.deposit_amount}</span></div>
              </div>
            )}
            {isQuote ? null : paid ? (
              <div className="mt-6 flex items-center gap-2 text-white bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-sm" data-testid="deposit-paid-badge">
                <CheckCircle2 className="h-4 w-4" /> {t("detail.depositPaidBadge")}
              </div>
            ) : (
              <Button data-testid="pay-deposit-btn" onClick={payDeposit} disabled={paying}
                className="w-full mt-6 h-12 rounded-full bg-white text-black hover:bg-white/85">
                {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-2" /> {t("detail.payDeposit", { amount: booking.deposit_amount })}</>}
              </Button>
            )}
            <a href={googleCalendarUrl(booking)} target="_blank" rel="noopener noreferrer" data-testid="add-to-calendar-link">
              <Button variant="outline"
                className="w-full mt-3 h-12 rounded-full border-white/20 bg-transparent text-white hover:bg-white/10">
                <CalendarPlus className="h-4 w-4 mr-2" /> {t("detail.addCalendar")}
              </Button>
            </a>
            <Button variant="ghost" className="w-full mt-3 text-muted-foreground hover:text-white hover:bg-white/5"
              onClick={() => navigate("/account")}>{t("detail.myReservations")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
